/**
 * @module support/shared/widgets
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */ /** */

// tslint:disable:max-file-line-count

// no-forward-ref Allows to refer to references which are not yet defined. Here it cannot be removed as
// InputComponent is not yet defined.
/* tslint:disable:no-forward-ref */

// Cannot be removed as input aria-label renamed to arialabel is used and has may instances.
// Moreover aria-label cannot be used as  it has an '-' which cannot be used as a variable name
/* tslint:disable:no-input-rename */

import {
  Component,
  forwardRef,
  Input,
  Output,
  EventEmitter,
  Provider,
  OnDestroy,
  OnInit,
  SimpleChanges,
  OnChanges,
  PipeTransform,
} from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { CommonApiService } from '@support/shared/services/common-api/common-api.service';
import { FieldValidationItem } from '@support/shared/models/field-validation-item.model';
import { DeviceDetectionService } from '@support/shared/widgets/services/device-detection.service';

/**
 * Provider Expression that allows tmo-checkbox to register as a ControlValueAccessor.
 * This allows it to support [(ngModel)].
 * @docs-private
 */
export const CHECKBOX_CONTROL_VALUE_ACCESSOR: Provider = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => InputComponent),
  multi: true,
};

/**
 * A T-mobile custom design input component. This component supports functionality for text, number, email,
 * password input fields and exposes the HTML5 API for this component.
 *
 * This component is customizable for input component attributes: type, placeholder, error-style class, error-text,
 * id, name, required, tabIndex, attr.aria-label and attr.aria-labelledby. NOTE: All attrinutes are not required and
 * if optional attrinutes are not passed the component will assign default values.
 */
@Component({
  selector: 'tmo-input',
  templateUrl: './input.component.html',
  styleUrls: ['../../../../../../style-sheets/components/input.component.scss'],
  providers: [CHECKBOX_CONTROL_VALUE_ACCESSOR],
})
export class InputComponent implements ControlValueAccessor, OnDestroy, OnInit, OnChanges {
  /**
   * Sets the type for the input box (text, email, password). Default value assigned
   * to input is type text.
   */
  @Input() public type?: string;

  /**
   * Sets the type of Scanner being used
   */
  @Input() public barCodeInput?: boolean;
  /**
   * Input that sets autofocus of the field
   *
   */
  @Input() public autoFocus?: boolean;

  /**
   * Input property to track if the text ellipsis or not.
   *
   */
  @Input() public isEllipsis?: boolean;

  /**
   * A unique id for input, if not supplied ID will not be added.
   * Best practice is to always provide an Id.
   */
  @Input() public id?: string;
  /**
   * Name input property to be assigned to the input attribute.
   * IMPORTANT : if it is used inside a <form>, name has to be unique
   */
  @Input() public name?: string;

  /**
   * Attribute to display placeholder text when the input is not filled in
   */
  @Input() public placeholder?: string;

  /**
   * Attribute to display Input V3 style
   */
  @Input() public v3Input?: boolean;

  /**
   * Attribute to hide error message
   */
  @Input() public hideInfieldErrorMessage?: boolean;

  /**
   * This property enables the placholder error trigger on the input field
   */
  @Input() public validationError?: boolean;

  /**
   * This property enables a class property for the input component
   */
  @Input() public inputTooltip?: boolean;

  /**
   * This property enables a align class property for the input component
   */
  @Input() public alignInput?: boolean;

  /**
   * Attribute to display the validation error text
   */
  @Input() public validationErrorText?: string;

  /**
   * Boolean input property to disable the input.
   */
  @Input() public disabled?: boolean;

  /**
   * Boolean input property to make a checkbox mandatory, if used with a <form>
   */
  @Input() public required?: boolean;

  /**
   * String property to match input with given pattern,  if used with a <form>
   */
  @Input() public pattern?: string;

  /**
   * Numeric input property to add indexing order, to make input accessible through keyboard
   */
  @Input() public tabindex?: number;
  /**
   * Attached to the aria-label attribute of the host element. In most cases, arial-labelledby will
   * take precedence so this may be omitted.
   */
  @Input('aria-label') public ariaLabel?: string;
  /**
   * Users can specify the `aria-labelledby` attribute which will be forwarded to the input element
   */
  @Input('aria-labelledby') public ariaLabelledby?: string;

  /** Event emitted when the input is tabbed out.
   * It emits the 'Event' object.
   */
  @Output() public blur: EventEmitter<{}>;

  /** Event emitted when the input is tabbed out.
   * It emits the 'Event' object.
   */
  @Output() public focus: EventEmitter<{}>;
  @Output() public keydown: EventEmitter<{}>;
  /**
   * Users can specify this field to impose the type of the field this input element is
   * and to fetch error message if it is dirty
   */
  @Input() public fieldType?: string;
  /**
   * Attribute to display the validation error text
   */
  @Input() public customErrorText?: string;
  /**
   * This field determines if the element is dirty or not as per the validations applied
   */
  @Input() public isInputDirty?: boolean;

  @Input() public fieldOptional?: boolean;

  /**
   * String property to set Max Length
   */
  @Input() public maxlength?: string;
  /**
   * String property to set Min Length
   */
  @Input() public minlength?: string;
  /**
   * boolean to show bau input for tablets
   */
  @Input() public showBauInput?: boolean;
  /**
   * Text alignment to the right
   */
  @Input() public textAlignmentRight?: boolean;

  @Input() public isRetailPlan?: boolean;

  /**
   * Displays validation error with padding to accommodate search glass
   *
   * @type {boolean}
   * @memberof InputComponent
   */
  @Input() public search?: boolean;

  @Input()
  public pipe?: PipeTransform;
  /**
   * Stores the filedValidationObject if the fieldType is passed
   */
  public fieldValidationObject: FieldValidationItem;
  /**
   * Boolean to keep track if element is in focus
   */
  public isFocused: boolean;

  /** Internal property to store current state of input box. */
  private innerValue: {};
  constructor(
    private commonApiService: CommonApiService,
    private deviceDetectionService: DeviceDetectionService
  ) {
    this.disabled = false;
    this.required = false;
    this.minlength = '';
    this.maxlength = '';
    this.isInputDirty = false;
    this.fieldOptional = false;
    this.fieldType = '';
    this.innerValue = '';
    this.blur = new EventEmitter<{}>();
    this.focus = new EventEmitter<{}>();
    this.keydown = new EventEmitter<{}>();
    this.ariaLabelledby = '';
    this.ariaLabel = '';
    this.tabindex = 0;
    this.pattern = '';
    this.v3Input = false;
  }
  public ngOnInit(): void {
    if (this.fieldType) {
      this.fieldValidationObject = this.commonApiService.getFieldValidation(this.fieldType);
      this.checkInputType();
    }
  }

  /**
   * Function called whenever input value changes
   * @param changes - Angular interface SimpleChanges
   * @docs - public
   */
  public ngOnChanges(changes: SimpleChanges): void {
    if (changes && changes.isInputDirty) {
      const chng = changes.isInputDirty;
      const cur = JSON.stringify(chng.currentValue);
      const prev = JSON.stringify(chng.previousValue);
      if (cur !== prev && !changes.isInputDirty.firstChange) {
        this.validateInput();
      }
    }
    if (changes && changes.fieldType) {
      this.fieldValidationObject = this.commonApiService.getFieldValidation(this.fieldType);
    }
  }
  /**
   * ngModel accessor to get the value of property [based on input type]
   * @docs-private
   */
  get value(): {} {
    return this.innerValue;
  }

  /**
   * ngModel accessor to set the value
   * @param value - {} value: could be number or string
   * @docs - private
   */
  set value(value: {}) {
    if (value !== this.innerValue) {
      value = this.pipe ? this.pipe.transform(value) : value;
      if (
        this.id &&
        this.id === 'dollar_amount' &&
        !String(value).match(this.fieldValidationObject.regex)
      ) {
        value = this.innerValue;
      }
      this.innerValue = value;
      this.onChangeCallback(value);
    }
  }

  /**
   * Called whenever the input is focused out
   */
  public onBlur(): void {
    this.isFocused = false;
    this.validateInput();
    this.blur.emit(this.innerValue);
    this.onTouchedCallback();
  }

  /**
   * Called whenever the input is focused in
   */
  public onFocus(): void {
    this.isFocused = true;
    this.focus.emit(this.innerValue);
    this.onTouchedCallback();
  }

  /**
   * Sets the model value. Implemented as part of ControlValueAccessor.
   * @param value Value to be set to the model.
   */
  public writeValue(value: Object): void {
    if (value !== this.innerValue) {
      this.validateInput();
      this.innerValue = value;
      value = this.innerValue;
      this.onChangeCallback(value);
    }
  }

  /**
   * Registers a callback to be triggered when the value has changed.
   * Implemented as part of ControlValueAccessor.
   * @param fn Function to be called on change.
   */
  public registerOnChange(fn: (_: boolean) => {}): void {
    this.onChangeCallback = fn;
  }

  /**
   * Registers a callback to be triggered when the control has been touched.
   * Implemented as part of ControlValueAccessor.
   * @param fn Callback to be triggered when the input is touched.
   */
  public registerOnTouched(fn: () => {}): void {
    this.onTouchedCallback = fn;
  }

  public ngOnDestroy(): void {}

  /**
   * Called when the input is blurred. Needed to properly implement ControlValueAccessor.
   * @docs-private
   */
  private onTouchedCallback(): void {}

  /**
   * Called when the input value is changed. Needed to properly implement ControlValueAccessor.
   * @docs-private
   */
  private onChangeCallback(_: {}): void {}
  /**
   * Called when the input value is changed to identify if the validations are met
   * @docs-private
   * @returns void
   */
  private validateInput(): void {
    this.validationError = this.isInputDirty;
    // @TODO refactor it to not apply the validation always
    if (this.fieldType !== 'BYPASS') {
      if (this.isInputDirty) {
        // tslint:enable no-string-literal
        this.validationErrorText =
          this.validationErrorText ||
          this.customErrorText ||
          (this.fieldValidationObject &&
            // @TODO FIX_TS_LINT
            // tslint:disable-next-line:no-string-literal
            this.fieldValidationObject.errorMessages['default']['errorMessage']);
        // tslint:disable no-string-literal
      } else {
        this.validationErrorText = '';
      }
    }
  }

  /**
   * method to assign input value type
   */
  private checkInputType(): void {
    const isMobileDevice = this.deviceDetectionService.isMobile;
    const isTablet = this.deviceDetectionService.isTablet;
    const patternRegex = this.pattern ? new RegExp(this.pattern) : null;
    const charRegex = this.checkRegExp() || null;
    const alphaNumericValue = 'abc';
    if (!this.showBauInput) {
      if (
        (isMobileDevice || isTablet) &&
        ((patternRegex && !patternRegex.test(alphaNumericValue)) ||
          (charRegex && !charRegex.test(alphaNumericValue)))
      ) {
        this.type = 'number';
      }
    }
  }

  /**
   * method to check if field validation constants are present
   * @returns RegExp
   */
  private checkRegExp(): RegExp {
    return this.fieldValidationObject && new RegExp(this.fieldValidationObject.allowedChars);
  }

  /**
   * Called whenever the keydown on input
   */
  public onKeydown(event: KeyboardEvent): void {
    this.keydown.emit(event);
  }
}
